/**
 * Sort Lines Tool
 * 
 * Sorts lines of text with various options.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const SortLinesTool: ITransformerTool = {
  id: 'sort-lines',
  name: 'Sort Lines',
  description: 'Sort lines alphabetically, numerically, or by length',
  version: '1.0.0',
  type: 'transformer',
  category: 'Text Tools',
  keywords: ['sort', 'lines', 'alphabetical', 'order', 'arrange', 'text'],

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'sortType',
      type: 'select',
      label: 'Sort Type',
      defaultValue: 'alphabetical',
      options: ['alphabetical', 'numeric', 'length'],
      description: 'How to sort the lines',
    },
    {
      name: 'order',
      type: 'select',
      label: 'Order',
      defaultValue: 'ascending',
      options: ['ascending', 'descending'],
      description: 'Sort order',
    },
    {
      name: 'caseSensitive',
      type: 'boolean',
      label: 'Case Sensitive',
      defaultValue: false,
      description: 'Consider case when sorting alphabetically',
    },
    {
      name: 'removeDuplicates',
      type: 'boolean',
      label: 'Remove Duplicates',
      defaultValue: false,
      description: 'Remove duplicate lines',
    },
    {
      name: 'ignoreEmpty',
      type: 'boolean',
      label: 'Ignore Empty Lines',
      defaultValue: false,
      description: 'Remove empty lines from output',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const sortType = (config.sortType as string) || 'alphabetical';
    const order = (config.order as string) || 'ascending';
    const caseSensitive = config.caseSensitive as boolean;
    const removeDuplicates = config.removeDuplicates as boolean;
    const ignoreEmpty = config.ignoreEmpty as boolean;

    let lines = input.split('\n');

    // Remove empty lines if requested
    if (ignoreEmpty) {
      lines = lines.filter(line => line.trim() !== '');
    }

    // Remove duplicates if requested
    if (removeDuplicates) {
      lines = [...new Set(lines)];
    }

    // Sort based on type
    lines.sort((a, b) => {
      let compareA = a;
      let compareB = b;

      if (sortType === 'alphabetical') {
        if (!caseSensitive) {
          compareA = a.toLowerCase();
          compareB = b.toLowerCase();
        }
        return compareA.localeCompare(compareB);
      } else if (sortType === 'numeric') {
        const numA = parseFloat(a) || 0;
        const numB = parseFloat(b) || 0;
        return numA - numB;
      } else if (sortType === 'length') {
        return a.length - b.length;
      }
      return 0;
    });

    // Reverse for descending
    if (order === 'descending') {
      lines.reverse();
    }

    return lines.join('\n');
  },
};

